import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  TrendingUp,
  LayoutDashboard,
  Layers,
  ChefHat,
  MonitorSmartphone,
  ArrowRight,
  Check,
  Menu,
  X,
  UtensilsCrossed,
} from "lucide-react";
import logo from "@/assets/logo.png";

export const Route = createFileRoute("/")({
  beforeLoad: async ({ context }) => {
    const isDesktop = context.isDesktop;

    const session = context.session;

    if (isDesktop) {
      if (!session || !session.data) {
        throw redirect({
          to: "/login",
        });
      }
      if (session.data.user.role === "admin") {
        throw redirect({
          to: "/superadmin",
        });
      }
      throw redirect({
        to: "/dashboard",
      });
    }

    return { session };
  },
  component: LandingPage,
});

function LandingPage() {
  const { session } = Route.useRouteContext();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"floor" | "menu" | "reports">("floor");

  const features = [
    {
      icon: ChefHat,
      title: "Hızlı Menü & Sipariş Yönetimi",
      description:
        "Kategoriler, varyantlar ve reçeteler ile menünüzü dakikalar içinde oluşturun. Garsonlarınız siparişleri saniyeler içinde alsın.",
    },
    {
      icon: LayoutDashboard,
      title: "İnteraktif Kat Planı Tasarımcısı",
      description:
        "Sürükle-bırak arayüzü ile restoranınızın kat planını görsel olarak çizin. Masaların doluluk durumunu anlık olarak izleyin.",
    },
    {
      icon: TrendingUp,
      title: "Gelişmiş Analitik & Raporlama",
      description:
        "Günlük ciro, en çok satan ürünler, garson performansları ve kar marjlarınızı anlık grafiklerle detaylıca takip edin.",
    },
    {
      icon: MonitorSmartphone,
      title: "Masaüstü & Mobil Cihaz Uyumlu",
      description:
        "Personeliniz için masaüstü terminal uygulaması, işletme sahipleri için her yerden erişilebilen bulut tabanlı web yönetim paneli.",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-indigo-500 selection:text-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Vidyon Logo" className="h-9 w-auto object-contain" />
            <div className="h-6 w-px bg-slate-200" />
            <span className="text-xl font-bold tracking-tight bg-gradient-to-r from-slate-900 to-indigo-900 bg-clip-text text-transparent">
              POS
            </span>
          </div>

          <div className="hidden md:flex items-center gap-8">
            <a
              href="#features"
              className="text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors"
            >
              Özellikler
            </a>
            <a
              href="#demo"
              className="text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors"
            >
              Panel Önizleme
            </a>
            <a
              href="#pricing"
              className="text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors"
            >
              Fiyatlandırma
            </a>
          </div>

          <div className="hidden md:flex items-center gap-4">
            {session && session.data ? (
              <Link
                to={session.data.user.role === "admin" ? "/superadmin" : "/dashboard"}
                className="inline-flex items-center justify-center px-5 h-11 rounded-xl text-sm font-semibold bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-100 transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 cursor-pointer"
              >
                Panelime Git
                <ArrowRight className="ml-2 size-4" />
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm font-semibold text-slate-700 hover:text-indigo-600 transition-colors cursor-pointer"
                >
                  Giriş Yap
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center px-5 h-11 rounded-xl text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white shadow-md transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 cursor-pointer"
                >
                  Hemen Başla
                </Link>
              </>
            )}
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 -mr-2 md:hidden text-slate-600 hover:text-indigo-600"
          >
            {mobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-b border-slate-100 bg-white p-6 absolute top-20 left-0 w-full shadow-lg flex flex-col gap-4 animate-in fade-in slide-in-from-top-5 duration-200">
            <a
              href="#features"
              onClick={() => setMobileMenuOpen(false)}
              className="text-base font-semibold text-slate-700 hover:text-indigo-600"
            >
              Özellikler
            </a>
            <a
              href="#demo"
              onClick={() => setMobileMenuOpen(false)}
              className="text-base font-semibold text-slate-700 hover:text-indigo-600"
            >
              Panel Önizleme
            </a>
            <a
              href="#pricing"
              onClick={() => setMobileMenuOpen(false)}
              className="text-base font-semibold text-slate-700 hover:text-indigo-600"
            >
              Fiyatlandırma
            </a>
            <hr className="border-slate-100 my-2" />
            {session && session.data ? (
              <Link
                to={session.data.user.role === "admin" ? "/superadmin" : "/dashboard"}
                className="w-full inline-flex items-center justify-center px-5 h-12 rounded-xl text-base font-semibold bg-indigo-600 text-white"
              >
                Panelime Git
                <ArrowRight className="ml-2 size-4" />
              </Link>
            ) : (
              <div className="flex flex-col gap-3">
                <Link
                  to="/login"
                  className="w-full inline-flex items-center justify-center px-5 h-12 rounded-xl text-base font-semibold border border-slate-200 text-slate-700 hover:bg-slate-50"
                >
                  Giriş Yap
                </Link>
                <Link
                  to="/login"
                  className="w-full inline-flex items-center justify-center px-5 h-12 rounded-xl text-base font-semibold bg-slate-900 text-white"
                >
                  Hemen Başla
                </Link>
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-20 pb-24 md:pt-32 md:pb-36 bg-gradient-to-b from-indigo-50/40 via-white to-slate-50/50">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[1000px] h-[350px] bg-gradient-to-tr from-indigo-400/10 to-violet-400/10 rounded-full blur-[140px]" />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs font-semibold tracking-wide uppercase mb-6 animate-pulse">
            <UtensilsCrossed className="size-3.5" /> Yeni Nesil Bulut POS Sistemi
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-slate-900 max-w-4xl mx-auto leading-[1.1]">
            İşletmenizin Sipariş ve Ödeme Akışını{" "}
            <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
              Kusursuzlaştırın
            </span>
          </h1>

          <p className="mt-8 text-lg md:text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed">
            Restoran ve cafeler için özel tasarlanmış interaktif kat planı, dinamik menü yönetimi ve
            detaylı analitik raporlar tek bir bulut tabanlı platformda.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/login"
              className="w-full sm:w-auto inline-flex items-center justify-center px-8 h-14 rounded-2xl text-base font-bold bg-indigo-600 hover:bg-indigo-500 text-white shadow-xl shadow-indigo-100 hover:shadow-indigo-200 transition-all duration-200 hover:-translate-y-0.5 cursor-pointer"
            >
              Ücretsiz Deneyin
              <ArrowRight className="ml-2.5 size-5" />
            </Link>
            <a
              href="#demo"
              className="w-full sm:w-auto inline-flex items-center justify-center px-8 h-14 rounded-2xl text-base font-bold bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all duration-200 hover:-translate-y-0.5"
            >
              Paneli İncele
            </a>
          </div>

          {/* Large Hero App Preview */}
          <div className="mt-16 md:mt-20 border border-slate-200/60 rounded-[32px] overflow-hidden bg-white p-3 shadow-2xl max-w-5xl mx-auto group">
            <div className="border border-slate-100 rounded-[22px] overflow-hidden bg-slate-950 aspect-[16/9] flex items-center justify-center relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-indigo-950 via-slate-900 to-violet-950 flex flex-col items-center justify-center p-12 text-white">
                <LayoutDashboard className="size-16 text-indigo-400 animate-bounce mb-4" />
                <span className="text-xl md:text-2xl font-bold tracking-tight">
                  Vidyon POS Yönetim Paneli
                </span>
                <p className="text-sm text-slate-400 mt-2 max-w-md text-center">
                  Web panelinden şubelerinizi, masalarınızı ve personelinizi yönetin. Her yerden
                  anlık satışlarınızı takip edin.
                </p>
                <div className="mt-8 flex gap-3">
                  <div className="h-2 w-2 rounded-full bg-red-500" />
                  <div className="h-2 w-2 rounded-full bg-yellow-500" />
                  <div className="h-2 w-2 rounded-full bg-green-500" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
            <h2 className="text-xs font-bold uppercase tracking-widest text-indigo-600 mb-3">
              Güçlü Özellikler
            </h2>
            <p className="text-3xl md:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight">
              Restoranınızı Yönetmek Artık Çok Daha Kolay
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className="p-8 rounded-3xl bg-slate-50 border border-slate-100 hover:border-indigo-100 hover:bg-indigo-50/10 transition-all duration-300 group"
              >
                <div className="inline-flex items-center justify-center size-12 rounded-2xl bg-indigo-50 text-indigo-600 mb-6 group-hover:bg-indigo-600 group-hover:text-white transition-colors duration-300">
                  <feature.icon className="size-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-950 mb-3">{feature.title}</h3>
                <p className="text-slate-500 leading-relaxed text-sm md:text-base">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Tabs Showcase */}
      <section id="demo" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-xs font-bold uppercase tracking-widest text-indigo-600 mb-3">
              Arayüz Detayları
            </h2>
            <p className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
              Kullanıcı Dostu Tasarım
            </p>
          </div>

          <div className="flex items-center justify-center gap-2 mb-12">
            <button
              onClick={() => setActiveTab("floor")}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer ${
                activeTab === "floor"
                  ? "bg-slate-900 text-white"
                  : "bg-white text-slate-600 hover:bg-slate-100"
              }`}
            >
              Kat Planı
            </button>
            <button
              onClick={() => setActiveTab("menu")}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer ${
                activeTab === "menu"
                  ? "bg-slate-900 text-white"
                  : "bg-white text-slate-600 hover:bg-slate-100"
              }`}
            >
              Menü Düzenleyici
            </button>
            <button
              onClick={() => setActiveTab("reports")}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer ${
                activeTab === "reports"
                  ? "bg-slate-900 text-white"
                  : "bg-white text-slate-600 hover:bg-slate-100"
              }`}
            >
              Anlık Raporlar
            </button>
          </div>

          <div className="bg-white border border-slate-200 rounded-[32px] p-6 shadow-xl max-w-4xl mx-auto aspect-video flex items-center justify-center text-center">
            {activeTab === "floor" && (
              <div>
                <Layers className="size-16 text-indigo-600 mx-auto mb-4 animate-pulse" />
                <h4 className="text-2xl font-bold text-slate-900">İnteraktif Kat Planı Modülü</h4>
                <p className="text-slate-500 mt-2 max-w-md mx-auto text-sm leading-relaxed">
                  Salon, bahçe ve teras katlarındaki masaları görsel olarak yerleştirin, doluluk
                  sürelerini ve sipariş tutarlarını anlık renk kodları ile izleyin.
                </p>
              </div>
            )}
            {activeTab === "menu" && (
              <div>
                <ChefHat className="size-16 text-indigo-600 mx-auto mb-4 animate-pulse" />
                <h4 className="text-2xl font-bold text-slate-900">
                  Menü ve Fiyatlandırma Yönetimi
                </h4>
                <p className="text-slate-500 mt-2 max-w-md mx-auto text-sm leading-relaxed">
                  Fiyatları saniyeler içinde güncelleyin, ürün varyantları (büyük, orta, acılı)
                  ekleyin ve porsiyon bazlı stok takibi yapın.
                </p>
              </div>
            )}
            {activeTab === "reports" && (
              <div>
                <TrendingUp className="size-16 text-indigo-600 mx-auto mb-4 animate-pulse" />
                <h4 className="text-2xl font-bold text-slate-900">
                  Gerçek Zamanlı Ciro Grafikleri
                </h4>
                <p className="text-slate-500 mt-2 max-w-md mx-auto text-sm leading-relaxed">
                  Ödeme türlerine göre dağılım (Nakit, Kredi Kartı, Yemek Kartları), saatlik
                  yoğunluk oranları ve ciro hedeflerini canlı panelden izleyin.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-xs font-bold uppercase tracking-widest text-indigo-600 mb-3">
              Fiyatlandırma
            </h2>
            <p className="text-3xl md:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight">
              İşletmenizin İhtiyacına Uygun Paketler
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Starter Plan */}
            <div className="p-8 rounded-[32px] bg-slate-50 border border-slate-100 flex flex-col justify-between">
              <div>
                <span className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Starter
                </span>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold text-slate-950">$49</span>
                  <span className="text-slate-400 font-medium">/aylık</span>
                </div>
                <p className="mt-4 text-sm text-slate-500 leading-relaxed">
                  Küçük kafeler ve tek şubeli işletmeler için ideal başlangıç paketi.
                </p>
                <hr className="my-6 border-slate-200" />
                <ul className="space-y-3">
                  {[
                    "Maksimum 5 Masa",
                    "Tek Şube Yönetimi",
                    "Standart Menü Düzenleyici",
                    "E-Posta Desteği",
                  ].map((feat, i) => (
                    <li key={i} className="flex items-center gap-2.5 text-slate-600 text-sm">
                      <Check className="size-4 text-indigo-600 shrink-0" />
                      {feat}
                    </li>
                  ))}
                </ul>
              </div>
              <Link
                to="/login"
                className="mt-8 w-full inline-flex items-center justify-center h-12 rounded-xl text-sm font-bold border border-slate-200 hover:bg-slate-100 text-slate-700 transition-colors"
              >
                Başla
              </Link>
            </div>

            {/* Pro Plan (Recommended) */}
            <div className="p-8 rounded-[32px] bg-slate-900 border border-slate-800 text-white flex flex-col justify-between shadow-xl relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-indigo-600 text-[10px] font-extrabold tracking-wider uppercase text-white px-2.5 py-1 rounded-full">
                ÖNERİLEN
              </div>
              <div>
                <span className="text-sm font-bold uppercase tracking-wider text-indigo-400">
                  Pro
                </span>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold text-white">$99</span>
                  <span className="text-slate-400 font-medium">/aylık</span>
                </div>
                <p className="mt-4 text-sm text-slate-400 leading-relaxed">
                  Büyümekte olan restoranlar ve masalı servis sunan kafeler için en popüler paket.
                </p>
                <hr className="my-6 border-slate-800" />
                <ul className="space-y-3">
                  {[
                    "Sınırsız Masa & Kat Planı",
                    "Çoklu Şube Yönetimi",
                    "Gelişmiş Menü ve Varyantlar",
                    "Anlık Raporlar & Grafik Detayları",
                    "7/24 Telefon & WhatsApp Desteği",
                  ].map((feat, i) => (
                    <li key={i} className="flex items-center gap-2.5 text-slate-300 text-sm">
                      <Check className="size-4 text-indigo-400 shrink-0" />
                      {feat}
                    </li>
                  ))}
                </ul>
              </div>
              <Link
                to="/login"
                className="mt-8 w-full inline-flex items-center justify-center h-12 rounded-xl text-sm font-bold bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-900/40 transition-colors"
              >
                Hemen Başla
              </Link>
            </div>

            {/* Enterprise Plan */}
            <div className="p-8 rounded-[32px] bg-slate-50 border border-slate-100 flex flex-col justify-between">
              <div>
                <span className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Enterprise
                </span>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold text-slate-950">Özel</span>
                </div>
                <p className="mt-4 text-sm text-slate-500 leading-relaxed">
                  Çok şubeli zincir restoranlar ve franchising ağları için özelleştirilmiş çözümler.
                </p>
                <hr className="my-6 border-slate-200" />
                <ul className="space-y-3">
                  {[
                    "Sınırsız Şube Entegrasyonu",
                    "ERP & Muhasebe Programı Bağlantısı",
                    "Dedicated Sunucu & Veritabanı Seçeneği",
                    "Özel Destek Yöneticisi",
                    "API Erişimi & Özel Entegrasyonlar",
                  ].map((feat, i) => (
                    <li key={i} className="flex items-center gap-2.5 text-slate-600 text-sm">
                      <Check className="size-4 text-indigo-600 shrink-0" />
                      {feat}
                    </li>
                  ))}
                </ul>
              </div>
              <Link
                to="/login"
                className="mt-8 w-full inline-flex items-center justify-center h-12 rounded-xl text-sm font-bold border border-slate-200 hover:bg-slate-100 text-slate-700 transition-colors"
              >
                Bize Ulaşın
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img
              src={logo}
              alt="Vidyon Logo"
              className="h-7 w-auto object-contain brightness-0 invert opacity-60"
            />
            <span className="text-white font-bold text-sm tracking-wide">Vidyon POS</span>
          </div>
          <p className="text-xs">
            &copy; {new Date().getFullYear()} Vidyon TV. Tüm Hakları Saklıdır.
          </p>
        </div>
      </footer>
    </div>
  );
}
